/**
 * ai_agent — re-export shim over bunnyquery/engine.
 *
 * The chat request/transport/prompt logic now lives in the framework-agnostic
 * `bunnyquery/engine` package (shared with the BunnyQuery widget). This file
 * keeps the historical `@/code/ai_agent` import path working unchanged for the
 * rest of the app, wires the engine's transport to this app's skapi instance,
 * and keeps the two pieces of Vue-reactive, app-level shared state that are NOT
 * engine-owned (they are read/written across dbfile.vue, agent.vue, main.vue).
 */
import { reactive, ref } from 'vue';
import { skapi } from '@/main';
import { MCP_BASE_URL } from './mcp_oauth';
import { configureChatEngine, ChatSession, type ChatHost, type BgTaskEntry } from 'bunnyquery/engine';

// Inject this app's transport + MCP endpoint into the engine. `poll` is omitted:
// the npm-bundled skapi-js used here resolves clientSecretRequest with the final
// body directly (the BunnyQuery widget, on skapi-js@latest, sets poll: 0).
configureChatEngine({
	clientSecretRequest: (opts: any) => (skapi.clientSecretRequest as any)(opts),
	clientSecretRequestHistory: (params: any, fetchOptions: any) =>
		(skapi as any).clientSecretRequestHistory(params, fetchOptions),
	mcpBaseUrl: MCP_BASE_URL,
});

export * from 'bunnyquery/engine';

// Delete a file's AI-index record ("src::<storagePath>") ahead of a re-index or
// overwrite, so the agent re-creates it fresh instead of colliding/duplicating.
// The skapi backend cascades a src:: record delete to every record that
// references it (its reference-linked children), so this single call clears the
// file's whole reference-linked index subtree. Best-effort: a missing record (a
// file that was never indexed) or a permission/transport error must NOT fail the
// upload — the subsequent index request still runs and re-creates the record.
export async function deleteFileIndexRecord(service: string, storagePath: string): Promise<void> {
	if (!service || !storagePath) return;
	try {
		await (skapi as any).deleteRecords({ service, unique_id: 'src::' + storagePath });
	} catch (err) {
		console.warn('[ai_agent] deleteFileIndexRecord failed (non-fatal)', storagePath, err);
	}
}

// App-level shared state — NOT engine-owned. Populated by dbfile.vue immediately
// when notifyAgentSaveAttachment returns, drained by agent.vue to inject
// "Thinking…" bubbles, and watched by main.vue's fallback poller. Must stay
// Vue-reactive here so those components react to changes.
export const bgTaskQueue: BgTaskEntry[] = reactive([]);

// True while the agent chat view (agent.vue) is mounted. While it's open it owns
// polling/rendering of bgTaskQueue tasks; while closed, main.vue's fallback
// poller drives task completion so records still auto-refresh.
export const agentViewMounted = ref(false);

/* ---- shared ChatSession singleton (Stage E) -------------------------------
 * The chat state machine lives in bunnyquery/engine. agent.vue mounts/unmounts
 * on every service/tab switch, so the session is a module-level SINGLETON (its
 * lifetime = the app session): a per-component session would drop bgTaskQueue /
 * pendingAgentRequests / history-cache state on unmount and break the
 * dbfile.vue → queue → main.vue fallback-poller coordination.
 *
 * The host (identity, DOM, scroll, Service-class I/O) is owned by the VIEW, which
 * installs a concrete ChatHost on mount via setChatHost() and clears it on
 * unmount. The proxy throws only if an engine hook fires with no view mounted
 * (a late poll after unmount — the view guards those paths). */
let _liveHost: ChatHost | null = null;
const hostProxy = new Proxy({} as ChatHost, {
	get(_t, key) {
		if (!_liveHost) throw new Error('[ai_agent] chat host is not mounted');
		return (_liveHost as any)[key];
	},
});
export const chatSession = new ChatSession(hostProxy);
export function setChatHost(h: ChatHost | null) { _liveHost = h; }

// Make the engine's plain state POJO Vue-reactive so the view's
// computed(() => chatSession.state.<scalar>) track the engine's in-place writes.
// messages/attachments are additionally driven by shallowRef+triggerRef in the
// view (the engine mutates those arrays in place).
chatSession.state = reactive(chatSession.state);
// Share the bg-indexing queue (also read/written by dbfile.vue + main.vue) with
// the engine instance. aiChatHistoryCache + pendingAgentRequests are re-pointed
// at the project-list shared stores from within agent.vue (which imports them),
// avoiding an ai_agent ↔ project-list circular import here.
chatSession.bgTaskQueue = bgTaskQueue;
